import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {ApexOptions} from 'ng-apexcharts';
import {WidgetChartControllerComponent} from '../../../Controller/widget-chart-controller-component';
import {BarChartDto} from '../../../../Models/Chart/bar-chart.interface';

@Component({
  selector: 'app-widget-bar-chart',
  templateUrl: './widget-bar-chart.component.html',
  styleUrls: ['./widget-bar-chart.component.scss'],
  standalone: false
})
export class WidgetBarChartComponent extends WidgetChartControllerComponent implements OnChanges {

  @Input() data!: BarChartDto | null;

  chartOptions!: ApexOptions;


  /**
   * Se ejecuta si hay cambios en la variable 'data'
   *
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['data']) {
      this.initChart();
    }
  }


  /**
   * Inicializa la configuración completa del gráfico
   *
   */
  private initChart() {
    this.chartOptions = {
      ...this.getBaseChartConfig(),
      series: this.data?.series || [],
      xaxis: {
        categories: this.data?.categories || [],
        tickAmount: 15,
        labels: {
          trim: !this.data?.horizontal,
          maxHeight: 120,
          hideOverlappingLabels: this.data?.hideOverlappingLabels || false,
          style: {
            fontSize: '11px'
          },
          formatter: ((value: string): string => this.data?.horizontal ? this.setFormat(+value) : value),
        }
      },
      yaxis: this.buildYAxisConfig(),
      tooltip: {
        y: {
          formatter: (value: number, {seriesIndex}: any) => {
            const unit: string = this.data?.series?.[seriesIndex]?.['unit'] || '';
            return this.setFormat(value, unit);
          }
        }
      }
    };
  }


  /**
   * Configuración base del gráfico
   *
   */
  private getBaseChartConfig(): Partial<ApexOptions> {
    return {
      chart: {
        type: 'bar',
        height: 375,
        stacked: !this.hasDifferentUnits() ? (this.data?.stacked ?? true) : false,
        toolbar: {
          show: false,
        },
        animations: {
          enabled: false
        },
      },
      responsive: [{
        breakpoint: 480,
        options: {
          legend: {
            position: 'bottom',
            offsetX: -10,
            offsetY: 0
          }
        }
      }],
      plotOptions: {
        bar: {
          horizontal: this.data?.horizontal,
          borderRadius: 4,
          borderRadiusApplication: 'end',
        }
      },
      dataLabels: {
        enabled: false
      }
    };
  }


  /**
   * Configuración de ejes Y - maneja tanto single como multiple axis
   *
   */
  private buildYAxisConfig() {
    const needsMultipleYAxis: boolean = this.hasDifferentUnits() && this.data!.series.length > 1 && !this.data?.horizontal;

    if (!needsMultipleYAxis) {
      return {
        min: this.data?.min,
        max: this.data?.max,
        labels: {
          formatter: (value: number) => {

            if (this.data?.horizontal) {
              return String(value)
            }

            const unit: string = this.data?.series?.[0]?.['unit'];
            return this.setFormat(value, unit);
          }
        }
      };
    }

    const uniqueUnits = [...new Set(this.data?.series?.map((serie) => serie['unit']).filter(Boolean))];

    return this.data?.series?.map((serie, index) => {
      const unit: string = serie['unit'] || '';
      const unitIndex: number = uniqueUnits.indexOf(unit);
      const isFirstOfUnit: boolean = this.data?.series?.findIndex((serie) => serie['unit'] === unit) === index;

      return {
        seriesName: serie.name,
        show: isFirstOfUnit,
        opposite: unitIndex > 0,
        axisTicks: {
          show: isFirstOfUnit
        },
        axisBorder: {
          show: isFirstOfUnit
        },
        labels: {
          show: isFirstOfUnit,
          formatter: (value: number) => this.setFormat(value, unit)
        },
        title: isFirstOfUnit ? {text: unit, style: {fontSize: '12px'}} : undefined
      };
    });
  }


  /**
   * Determina si hay diferentes unidades en las series
   *
   */
  private hasDifferentUnits(): boolean {

    if (!this.data?.series || this.data?.series?.length <= 1) {
      return false;
    }

    const units = this.data?.series?.map((serie) => serie['unit']).filter(Boolean);
    const uniqueUnits = [...new Set(units)];

    return uniqueUnits.length > 1;
  }

}
